import { Agent } from '@monetize/types/app';

export const MN_ASSISTANT_HELLO_MESSAGE = `
**Hi, I'm Monty.**

Ask me anything about your quotes, billing, and customer data - no report building needed.

**Quotes**: Summarize quotes, explain pricing outcomes, and track approval or signature status.

**Billing**: Explain invoice charges, surface credits, and flag billing anomalies.

**Insights**: Renewals, forecasts, customer trends, and more.

Try asking:
- "Show renewals coming up in the next 90 days"
- "Which quotes are waiting on a signature?"
- "Summarize billing health for this month in 5 bullets"

What can I help you with?

*AI responses may not be 100% accurate. Please review before taking action.*
`;

export const METABASE_AGENT_HELLO_MESSAGE = `
Hello! I'm your reporting agent for real-time insights. Ask me anything like:

1. Who are our top 10 customers?
2. How many deals did we close last month?
3. What's our projected cash flow for the year?

Let’s work together to uncover valuable insights.
*Please note this agent is currently in Beta.*
`;

export const HELLO_MESSAGE_BY_AGENT: {
  [key in Agent]: string;
} = {
  [Agent.MONETIZE_NOW_ASSISTANT]: MN_ASSISTANT_HELLO_MESSAGE,
  [Agent.METABASE_AGENT]: METABASE_AGENT_HELLO_MESSAGE,
};

export const AGENT_OPTIONS = [
  { label: 'Quote-to-Cash Agent', value: Agent.MONETIZE_NOW_ASSISTANT },
  { label: 'Reporting Agent', value: Agent.METABASE_AGENT },
];

export const SUB_AGENT_TAGS = ['monetizenow-agent', 'metabase-agent'];

// Tags the `message` event carrying the response the backend says to display
// (a sub-agent's final answer when it ends the run without the supervisor).
export const RESPONSE_TAG = 'response';

export const CHAT_ERROR_MESSAGE =
  'Sorry, something went wrong. Please try again. If the issue persists, contact support.';

export const CHAT_RECONNECT_FAILED_MESSAGE =
  'The connection to the AI assistant kept dropping. Please try again.';

export const CHAT_STILL_PROCESSING_MESSAGE =
  'This is taking longer than usual. Refresh in a moment to check for your answer.';

export const PREFIX_TO_ENTITY = {
  quot: 'quote',
  invc: 'invoice',
  invce: 'invoice',
  bilg: 'bill_group',
  acct: 'account',
};

/**
 * Matches record IDs in the current URL that we can label for the chat service.
 * Deliberately narrower than the app-wide RECORD_ID_REGEX: it is derived from
 * PREFIX_TO_ENTITY so we never send the agent an entity name it has no mapping
 * for. Prefixes are sorted longest-first so `invce` wins over `invc`.
 */
export const CHAT_CONTEXT_ID_REGEX = new RegExp(
  `\\b(?:${Object.keys(PREFIX_TO_ENTITY)
    .sort((first, second) => second.length - first.length)
    .join('|')})_[A-Za-z0-9]+`,
  'g',
);

export const SESSION_ID_DISPLAY_LENGTH = 8;

export const CHAT_PANEL_MIN_WIDTH = 360;
export const CHAT_PANEL_DEFAULT_WIDTH = 420;
export const CHAT_PANEL_RESIZE_STEP = 24;

/**
 * The panel can grow until it meets the left sidebar, so its upper bound is
 * the viewport minus the sidebar's current width (never below the minimum).
 */
export const getChatPanelMaxWidth = (
  viewportWidth: number,
  sidebarWidth: number,
): number => Math.max(CHAT_PANEL_MIN_WIDTH, viewportWidth - sidebarWidth);

export const clampChatPanelWidth = (
  width: number,
  viewportWidth: number,
  sidebarWidth: number,
): number => {
  const rounded = Math.round(width);
  const requested = Number.isNaN(rounded) ? CHAT_PANEL_DEFAULT_WIDTH : rounded;
  return Math.min(
    getChatPanelMaxWidth(viewportWidth, sidebarWidth),
    Math.max(CHAT_PANEL_MIN_WIDTH, requested),
  );
};

/**
 * Agent tools whose completion means the quote on screen is now stale. A
 * `tool_end` for one of these refetches the open quote mid-turn, so the page
 * reflects the agent's work as it happens instead of only once the whole turn
 * settles (which only happens at all when the agent also emits a redirect
 * signal). Tool names come from the agent service -- add to this list as more
 * quote-mutating tools ship.
 */
export const QUOTE_REFRESH_TOOLS = new Set<string>([
  'create_quote_offering',
  'update_quote_offering',
  'update_quote',
  'create_net_new_quote',
  'create_account_based_rate',
  'create_or_delete_quote_offering_price_uplift',
]);

export const CHAT_LAUNCHER_TOOLTIP = 'Chat with Monty';
